import { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import {
  getQuickBooksSettings,
  updateQuickBooksSettings,
  getQuickBooksConnectUrl,
  getQuickBooksLogs,
  getFailedQuickBooksSyncs,
  syncCompanyToQuickBooks,
  syncInvoiceToQuickBooks,
  QuickBooksSyncLog,
} from '../../api/quickbooksService';
import { qbSyncBadgeClass } from '../../util/qbSyncStatus';

const formatDateTime = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString() : '—';

const QuickBooksSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<any>(null);
  const [autoSync, setAutoSync] = useState(true);
  const [environment, setEnvironment] = useState('sandbox');
  const [realmId, setRealmId] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [logs, setLogs] = useState<QuickBooksSyncLog[]>([]);
  const [failed, setFailed] = useState<QuickBooksSyncLog[]>([]);
  const [retrying, setRetrying] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsData, logsData, failedData] = await Promise.all([
        getQuickBooksSettings(),
        getQuickBooksLogs({ limit: 30 }),
        getFailedQuickBooksSyncs(),
      ]);
      setSettings(settingsData);
      setAutoSync(settingsData.auto_sync_enabled);
      setEnvironment(settingsData.environment || 'sandbox');
      setRealmId(settingsData.realm_id || '');
      setLogs(logsData);
      setFailed(failedData);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load QuickBooks settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectionResult = params.get('quickbooks');
    const connectionError = params.get('error');

    if (connectionResult === 'connected') {
      window.history.replaceState({}, document.title, window.location.pathname);
      load();
    } else if (connectionError) {
      setError(connectionError);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const { authorization_url } = await getQuickBooksConnectUrl(environment);
      if (!authorization_url) throw new Error('The server did not return an authorization URL.');
      window.location.assign(authorization_url);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to start QuickBooks connection.');
      setConnecting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateQuickBooksSettings({
        auto_sync_enabled: autoSync,
        environment,
        realm_id: realmId || undefined,
        refresh_token: refreshToken || undefined,
      });
      setRefreshToken('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = async (log: QuickBooksSyncLog) => {
    setRetrying(log.id);
    setError(null);
    try {
      if (log.entity_type === 'company') {
        await syncCompanyToQuickBooks(log.entity_id);
      } else if (log.entity_type === 'invoice') {
        await syncInvoiceToQuickBooks(log.entity_id);
      }
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Retry failed.');
    } finally {
      setRetrying(null);
    }
  };

  const status = settings?.status;

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">QuickBooks</h1>
        <p className="mt-1 text-sm text-gray-500">
          Sync magazine customers and invoices to QuickBooks Online.
        </p>
      </div>

      {loading ? (
        <PageCard><p className="text-sm text-gray-500">Loading...</p></PageCard>
      ) : (
        <>
          <PageCard className="mb-6">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-medium text-gray-900">Connection</h2>
              {status?.mock && (
                <span className="rounded bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-800">
                  Mock mode
                </span>
              )}
              {status?.connected ? (
                <span className="rounded bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800">
                  Connected
                </span>
              ) : (
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                  Not connected
                </span>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="text-sm font-medium text-gray-600">Environment</div>
                <div className="mt-1 text-sm text-gray-900 capitalize">{status?.environment || '—'}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600">Realm ID</div>
                <div className="mt-1 font-mono text-sm text-gray-900">{status?.realm_id || '—'}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600">Connected at</div>
                <div className="mt-1 text-sm text-gray-900">{formatDateTime(status?.connected_at)}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600">Token expires</div>
                <div className="mt-1 text-sm text-gray-900">{formatDateTime(status?.token_expires_at)}</div>
              </div>
            </div>

            <div className="mt-6 space-y-4 border-t border-gray-200 pt-4">
              {/* <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={autoSync}
                  onChange={(e) => setAutoSync(e.target.checked)}
                  disabled={saving}
                />
                Auto-sync on invoice create and status change
              </label> */}

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Environment</label>
                <select
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value)}
                  className="rounded border border-gray-300 px-3 py-2 text-sm"
                  disabled={saving}
                >
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </select>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleConnect}
                  disabled={connecting || saving}
                  className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {connecting ? 'Connecting…' : status?.connected ? 'Reconnect QuickBooks' : 'Connect QuickBooks'}
                </button>
                <p className="mt-2 text-xs text-gray-500">
                  Sign in to Intuit and select a company. The realm ID and refresh token will be saved securely by the server.
                </p>
              </div>
              {/* <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Realm ID</label>
                <input
                  type="text"
                  value={realmId}
                  onChange={(e) => setRealmId(e.target.value)}
                  placeholder="QuickBooks company ID"
                  className="w-full max-w-md rounded border border-gray-300 px-3 py-2 text-sm font-mono"
                  disabled={saving}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">Refresh token</label>
                <input
                  type="password"
                  value={refreshToken}
                  onChange={(e) => setRefreshToken(e.target.value)}
                  placeholder={settings?.has_refresh_token ? '•••••••• (leave blank to keep)' : 'Paste OAuth refresh token'}
                  className="w-full max-w-md rounded border border-gray-300 px-3 py-2 text-sm font-mono"
                  disabled={saving}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Or set QUICKBOOKS_REFRESH_TOKEN and QUICKBOOKS_REALM_ID in server env.
                </p>
              </div>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save settings'}
              </button> */}
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
          </PageCard>

          {failed.length > 0 && (
            <PageCard className="mb-6">
              <h2 className="mb-4 text-lg font-medium text-gray-900">Failed syncs</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-500">
                      <th className="py-2 pr-4">When</th>
                      <th className="py-2 pr-4">Entity</th>
                      <th className="py-2 pr-4">Action</th>
                      <th className="py-2 pr-4">Error</th>
                      <th className="py-2">Retry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failed.map((log) => (
                      <tr key={log.id} className="border-b border-gray-100">
                        <td className="py-2 pr-4 text-gray-600">{formatDateTime(log.createdAt)}</td>
                        <td className="py-2 pr-4 text-gray-900">
                          {log.entity_type} #{log.entity_id}
                        </td>
                        <td className="py-2 pr-4 text-gray-600">{log.action}</td>
                        <td className="py-2 pr-4 text-red-700">{log.error_message || '—'}</td>
                        <td className="py-2">
                          <button
                            type="button"
                            onClick={() => handleRetry(log)}
                            disabled={retrying === log.id}
                            className="text-sm text-torch-primary hover:underline disabled:opacity-50"
                          >
                            {retrying === log.id ? 'Retrying…' : 'Retry'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </PageCard>
          )}

          <PageCard>
            <h2 className="mb-4 text-lg font-medium text-gray-900">Recent sync log</h2>
            {logs.length === 0 ? (
              <p className="text-sm text-gray-500">No sync activity yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-500">
                      <th className="py-2 pr-4">When</th>
                      <th className="py-2 pr-4">Entity</th>
                      <th className="py-2 pr-4">Action</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2">QB ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} className="border-b border-gray-100">
                        <td className="py-2 pr-4 text-gray-600">{formatDateTime(log.createdAt)}</td>
                        <td className="py-2 pr-4 text-gray-900">
                          {log.entity_type} #{log.entity_id}
                        </td>
                        <td className="py-2 pr-4 text-gray-600">{log.action}</td>
                        <td className="py-2 pr-4">
                          <span className={`rounded px-2 py-0.5 text-xs font-medium ${qbSyncBadgeClass(log.status)}`}>
                            {log.status}
                          </span>
                        </td>
                        <td className="py-2 font-mono text-gray-600">{log.qb_entity_id || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PageCard>
        </>
      )}
    </Layout>
  );
};

export default QuickBooksSettings;
